<template>
	<TnCard
		border
		class="padding-horizontal-l padding-bottom-s"
		:class="{ 'padding-top-3xl': badge, 'padding-top-2xl': !badge }"
	>
		<!-- BADGE -->
		<template #badge>
			<TnBadge
				v-if="badge"
				size="xs"
			>
				{{ badge.text || badge.badgeText }}
			</TnBadge>
		</template>

		<template #default>
			<!-- PRICE -->
			<div class="card-wrapper">
				<p class="large-price">
					{{
						discountedPrice
							? formatPrice.oneTime(discountedPrice, { round: true })
							: formatPrice.oneTime(product.prices.withVat?.fullPrice?.price, { round: true })
					}}
				</p>
				<p
					v-if="discountedPrice"
					class="original-price"
				>
					Før: {{ formatPrice.oneTime(product.prices.withVat?.fullPrice?.price, { round: true }) }}
				</p>
				<TnParagraph
					responsive
					size="xs"
					class="disclaimer-text"
					v-if="hasActiveMemberPrice || activeTab === 'down-payment'"
				>
					{{ totalPriceWithSubscriptionDisclaimer }}
				</TnParagraph>
				<TnParagraph
					responsive
					size="xs"
					class="disclaimer-text"
					v-if="hasActiveMemberPrice"
				>
					{{ nonCustomerDisclaimerText }}
				</TnParagraph>

				<!-- SPLITT / BETAL ALT NÅ BUTTONS -->
				<TnParagraph
					v-if="activeTab === 'down-payment'"
					class="margin-top-l"
					size="s"
					bold
				>
					Ingen renter eller gebyrer!
				</TnParagraph>
				<div
					v-if="isDownPaymentAvailable"
					class="padding-vertical-l button-wrapper"
				>
					<button
						class="tab-button"
						:class="{ active: activeTab === 'down-payment' }"
						@click="activeTab = 'down-payment'"
					>
						Splitt
					</button>
					<button
						class="tab-button"
						:class="{ active: activeTab === 'full-price' }"
						@click="activeTab = 'full-price'"
					>
						Betal alt nå
					</button>
				</div>

				<!-- DOWN PAYMENT TAB -->
				<div
					v-if="activeTab === 'down-payment'"
					class="tab-content"
				>
					<ECommerceAmountUpFrontSelector
						v-if="isAmountUpFrontEligible"
						v-model="amountUpFront"
						:maxAmount="maxAmountUpFront"
						:amountIncrements="content.cashUpFront.amountIncrements"
					/>

					<ECommerceDownPaymentPlanSelector
						:downPaymentProducts="downPaymentProducts"
						v-model="selectedDownPaymentProduct"
					/>
				</div>

				<!-- FULL PRICE TAB (info text only to be displayed if it is a down payment eligible category -->
				<div
					v-else-if="isDownPaymentCategory"
					class="tab-content margin-bottom-m margin-top-xs"
				>
					<TnParagraph
						size="s"
						v-if="!isDownPaymentAvailable"
					>
						Kun fullpris tilgjengelig.
					</TnParagraph>
				</div>

				<!-- MISC -->
				<div
					v-if="expandableTexts?.list"
					class="expandable margin-top-m"
				>
					<div
						class="expandable-header"
						@click="expandableOpen = !expandableOpen"
					>
						<TnParagraph
							size="s"
							bold
						>
							{{ expandableTexts.header }}
						</TnParagraph>
						<TnIcon :name="expandableOpen ? 'chevron-up' : 'chevron-down'" />
					</div>
					<div
						class="expandable-body margin-top-m"
						:class="{ open: expandableOpen }"
					>
						<div>
							<TnList
								v-if="expandableTexts?.list"
								size="s"
							>
								<TnListItem
									v-for="text in expandableTexts.list"
									:key="text"
									:text="text"
								/>
							</TnList>
						</div>
					</div>
				</div>
			</div>
		</template>
	</TnCard>
</template>

<script setup lang="ts">
import EcommerceService from "~/integrations/ecommerce-v2-integration";
import { dynamicContent } from "~/plugins/content-plugin";
import dateHelper from "~/helpers/dateHelper";
import { useStore } from "vuex";
import { formatPrice } from "~/helpers/formatting/price";
import type { DownPaymentProduct } from "../ECommerceProductAccessories/types";
import type {
	DownPaymentExpandableTexts,
	FullPriceExpandableTexts,
	Schema,
} from "./ecommerce-product-payment-type-selector.d.ts";
import ECommerceDownPaymentPlanSelector from "./children/ECommerceDownPaymentPlanSelector.vue";
import { isCategoryEligibleForDownPayment } from "~/helpers/ecommerce/hardwareOrderHelper";

const { content } = defineProps<Schema>();

const { totalEstimate } = useTradeIn();

const badge = computed(
	() => product.value.prices?.memberPriceCampaign?.campaignData || product.value.prices?.campaign?.campaignData,
);

const activeTab = ref<"down-payment" | "full-price">();
const expandableOpen = ref<boolean>(false);
const selectedDownPaymentProduct = ref<DownPaymentProduct>();
const mobileSubscriptionPrice = ref(null);
const amountUpFront = ref<number>(0);

const { product } = useECommerceProduct();
const { isEligibleForDownPayment } = useIsEligibleForDownPayment();

const isDownPaymentCategory = computed<boolean>(() => isCategoryEligibleForDownPayment(product.value?.category));
const hasDownPaymentProducts = computed<boolean>(() => downPaymentProducts.value?.length > 0);
const isDownPaymentAvailable = computed<boolean>(() => isEligibleForDownPayment.value && hasDownPaymentProducts.value);

const store = useStore();

const totalPriceWithSubscriptionDisclaimer = computed(() =>
	dynamicContent({
		subscriptionPrice: formatPrice.oneTime(mobileSubscriptionPrice.value?.price || 0),
		fullPriceWithSubscription: formatPrice.oneTime(fullPriceWithSubscription.value),
	})(content?.totalPriceWithSubscriptionDisclaimer),
);

const hasSalePriceAndMemberPrice = computed(
	() => product.value.prices?.withVat?.fullPrice?.salePrice && hasActiveMemberPrice.value,
);

const nonCustomerDisclaimerText = computed(() =>
	dynamicContent({
		fullPrice: formatPrice.oneTime(
			hasSalePriceAndMemberPrice.value
				? product.value.prices.withVat.fullPrice.salePrice
				: product.value.prices.withVat.fullPrice.price,
		),
	})(content?.nonCustomerDisclaimerText),
);

const expandableTexts = computed(() => {
	if (activeTab.value === "down-payment" && content?.downPaymentExpandableTexts?.list?.length > 0) {
		return content?.downPaymentExpandableTexts as DownPaymentExpandableTexts;
	} else if (activeTab.value === "full-price" && content?.fullPriceExpandableTexts?.list?.length > 0) {
		return content?.fullPriceExpandableTexts as FullPriceExpandableTexts;
	}

	return null;
});

const setSelectedWebShopOption = (webShopOption: string) => {
	try {
		store.commit("ecommerce/setSelectedWebShopOption", webShopOption);
	} catch (error) {
		console.error("Error setting selected webshop option", error);
	}
};

const setSelectedDownPaymentProduct = () => {
	store.commit("ecommerce/setSelectedDownPaymentProduct", selectedDownPaymentProduct.value);
};

const getMobileSubscriptionPrice = async () => {
	try {
		return await EcommerceService.getMobileSubscriptionPrice();
	} catch (error) {
		console.error(error);
	}
};

const discountedPrice = computed(
	() =>
		product.value.prices?.withVat?.fullPrice?.memberPrice ||
		product.value.prices?.withVat?.fullPrice?.salePrice ||
		null,
);

const fullPriceWithSubscription = computed(() => {
	const subscriptionPrice = mobileSubscriptionPrice.value?.price || 0;
	const devicePrice =
		product.value.prices.withVat.fullPrice?.memberPrice ||
		product.value.prices.withVat.fullPrice.salePrice ||
		product.value.prices.withVat.fullPrice.price ||
		0;

	return devicePrice + subscriptionPrice;
});

const isAmountUpFrontEligible = computed(() => {
	const totalPrice = discountedPrice.value || product.value.prices.withVat.fullPrice.price;
	//this is because the minimum amount that needs to be left on the agreeemnt is 468
	return (
		hasDownPaymentProducts.value &&
		content?.cashUpFront?.enabled &&
		totalPrice - content?.cashUpFront?.amountIncrements - totalEstimate.value.total - 468 >
			content?.cashUpFront?.minimumAmountRemaining
	);
});

const maxAmountUpFront = computed(() => {
	if (isAmountUpFrontEligible.value === false) {
		return 0;
	}

	const floorToIncrement = (value, increment) => Math.floor(value / increment) * increment;

	const maxAmount = content?.cashUpFront?.maxAmount;
	const totalPrice = discountedPrice.value || product.value.prices.withVat.fullPrice.price;
	//this is because the minimum amount that needs to be left on the agreeemnt is 468

	const adjustedTotalPrice =
		totalPrice - content?.cashUpFront?.minimumAmountRemaining - totalEstimate.value.total - 468;
	const amountAvailableToPayUpFront = floorToIncrement(adjustedTotalPrice, content?.cashUpFront?.amountIncrements);

	return Math.min(amountAvailableToPayUpFront, maxAmount);
});

const hasActiveMemberPrice = computed(() => {
	const memberPriceCampaignData = product.value?.prices?.memberPriceCampaign;
	return dateHelper.todayIsBetween(
		new Date(memberPriceCampaignData?.startDate),
		new Date(memberPriceCampaignData?.endDate),
	);
});

const downPaymentProducts = computed(() => {
	return store.getters["ecommerce/downPaymentProducts"];
});

onMounted(async () => {
	store.commit("ecommerce/setDownPaymentProductsSortedBy", content?.sortDownPaymentPlansBy);

	mobileSubscriptionPrice.value = await getMobileSubscriptionPrice();

	// TODO: Set selected payment type based on url query parameter
	if (isDownPaymentAvailable.value) {
		activeTab.value = "down-payment";
		selectedDownPaymentProduct.value = downPaymentProducts.value[0];
		setSelectedWebShopOption(`DOWN_PAYMENT_${selectedDownPaymentProduct?.value?.monthlyInstallments}`);
	} else {
		activeTab.value = "full-price";
		setSelectedWebShopOption("FULL_PRICE");
	}
});

watch(activeTab, (newTab) => {
	if (newTab === "down-payment" && downPaymentProducts.value.length > 0 && !product.value?.disableDownPayment) {
		setSelectedWebShopOption(`DOWN_PAYMENT_${selectedDownPaymentProduct?.value?.monthlyInstallments}`);
	} else if (newTab === "full-price") {
		setSelectedWebShopOption("FULL_PRICE");
	}
});

watch(
	selectedDownPaymentProduct,
	async (newValue) => {
		store.commit("ecommerce/setSelectedDownPaymentProduct", newValue);
	},
	{ immediate: true },
);

watch(
	amountUpFront,
	(newValue) => {
		if (newValue > maxAmountUpFront.value) {
			newValue = maxAmountUpFront.value;
		}

		store.commit("ecommerce/setAmountUpFront", newValue);
		setSelectedDownPaymentProduct();
	},
	{ immediate: true },
);
// TODO: --never remove this-- So long and thanks for all the fish 🐟! Love you all! -Boye ♥️
</script>

<style scoped lang="scss">
.card-wrapper {
	width: 100%;
}
.large-price {
	font-size: 32px;
	line-height: 36px;
	color: $color-primary-mid;
	font-weight: 400;
	font-family: $font-family-telenor-base;
}

.original-price {
	text-decoration: line-through;
	font-family: $font-family-telenor-ui;
	color: $color-neutrals-600-shade;
	font-size: 14px;
	line-height: 20px;
	font-weight: 300;
}

.button-wrapper {
	display: flex;
	justify-content: start;
	gap: 16px;

	.tab-button {
		cursor: pointer;
		background-color: transparent;
		border: 1px solid $color-neutrals-300-tint;
		border-radius: 35px;
		color: $color-neutrals-900-shade;
		padding: 14px $spacing-l;
		font-size: 14px;
		line-height: 20px;
		font-weight: 300;
		font-family: $font-family-telenor-ui;

		&.active {
			color: $color-primary-mid;
			border: 2px solid $color-primary-mid;
		}
	}
}

.subscription-notice {
	font-family: $font-family-telenor-ui;
	color: $color-neutrals-900-shade;
	font-size: 14px;
	line-height: 20px;
	font-weight: 300;
}

.expandable {
	&-header {
		cursor: pointer;
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	&-body {
		display: grid;
		grid-template-rows: 0fr;
		overflow: hidden;
		transition: grid-template-rows 200ms;

		div {
			overflow: hidden;
		}

		&.open {
			grid-template-rows: 1fr;
		}
	}
}
</style>
